import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, FileText } from 'lucide-react';
import { espaceSalarieApi, BulletinResum } from '../api/espaceSalarieApi';

const MOIS_NOMS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];

const STATUT_LABELS: Record<string, { label: string; cls: string }> = {
  BROUILLON: { label: 'Brouillon', cls: 'bg-slate-100 text-slate-600' },
  GENERE: { label: 'Généré', cls: 'bg-blue-100 text-blue-700' },
  VALIDE: { label: 'Validé', cls: 'bg-emerald-100 text-emerald-700' },
  PAYE: { label: 'Payé', cls: 'bg-green-100 text-green-700' },
  ANNULE: { label: 'Annulé', cls: 'bg-red-100 text-red-600' },
};

const f = (n: number | string) => Number(n).toLocaleString('fr-FR') + ' F';

export function MesBulletinsPage() {
  const [bulletins, setBulletins] = useState<BulletinResum[]>([]);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);

  const now = new Date();
  const moisCourant = now.getMonth() + 1;
  const anneeCourante = now.getFullYear();

  useEffect(() => {
    setLoading(true);
    espaceSalarieApi.listerMesBulletins()
      .then(data => {
        setBulletins(data);
        setErreur(null);
      })
      .catch(() => setErreur('Impossible de charger les bulletins.'))
      .finally(() => setLoading(false));
  }, []);

  const bulletinMoisCourant = bulletins.find(
    b => b.mois === moisCourant && b.annee === anneeCourante
  );

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-64" />
          <div className="h-16 bg-slate-200 rounded-xl" />
          <div className="h-48 bg-slate-200 rounded-xl" />
        </div>
      </div>
    );
  }

  if (erreur) {
    return (
      <div className="max-w-3xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-700 font-semibold">{erreur}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <h1 className="text-2xl font-bold text-slate-900 mb-6">Mes bulletins de paie</h1>

      {/* Alerte si bulletin du mois courant absent */}
      {!bulletinMoisCourant && (
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
          <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <p className="text-amber-800 text-sm">
            Votre bulletin de{' '}
            <strong>{MOIS_NOMS[moisCourant - 1]} {anneeCourante}</strong>{' '}
            n'a pas encore été généré. Contactez votre gestionnaire.
          </p>
        </div>
      )}

      {/* Liste des bulletins */}
      {bulletins.length === 0 ? (
        <div className="bg-surface border border-slate-200 rounded-xl p-12 text-center">
          <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">Aucun bulletin disponible</p>
          <p className="text-slate-400 text-sm mt-1">Vos fiches de paie apparaîtront ici une fois générées.</p>
        </div>
      ) : (
        <div className="bg-surface border border-slate-200 rounded-xl overflow-hidden shadow-sm">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>
                {['Période', 'Salaire brut', 'Salaire net', 'Net à payer', 'Statut', ''].map(h => (
                  <th
                    key={h}
                    className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {bulletins.map(b => {
                const statut = STATUT_LABELS[b.statut] ?? { label: b.statut, cls: 'bg-slate-100 text-slate-600' };
                return (
                  <tr key={b.idBulletin} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-5 py-3 text-sm font-medium text-slate-900">
                      {MOIS_NOMS[b.mois - 1]} {b.annee}
                    </td>
                    <td className="px-5 py-3 text-sm font-mono tabular-nums text-slate-700">
                      {f(b.salaireBrut)}
                    </td>
                    <td className="px-5 py-3 text-sm font-mono tabular-nums text-slate-700">
                      {f(b.salaireNet)}
                    </td>
                    <td className="px-5 py-3 text-sm font-mono tabular-nums font-semibold text-emerald-700">
                      {f(b.netAPayer)}
                    </td>
                    <td className="px-5 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statut.cls}`}>
                        {statut.label}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <Link
                        to={`/espace-salarie/bulletins/${b.idBulletin}`}
                        className="text-xs font-medium text-emerald-600 hover:text-emerald-800 transition-colors"
                      >
                        Consulter
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default MesBulletinsPage;
